import type {
  ClearCardBankState,
  ExistingCashoutBankSelection,
  LowIncomeCacheData,
  ManualCashoutBankSelection,
  OutstandingLoanCacheItem,
  UnionLoanCacheData,
  UnionLoanCacheState,
  UnionLoanProductType,
} from '@/interface/unionLoan';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type {
  CashoutBankSelection,
  ClearCardBankIdentity,
  ClearCardBankState,
  ExistingCashoutBankSelection,
  LowIncomeCacheData,
  ManualCashoutBankSelection,
  OutstandingLoanCacheItem,
  UnionLoanCacheData,
  UnionLoanCacheState,
  UnionLoanProductType,
} from '@/interface/unionLoan';

const initialState: UnionLoanCacheState = {
  CL: {},
  KKC: {},
  CMC: {},
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const CACHE_KEYS = new Set([
  'cashoutAmount',
  'cashoutBankSelection',
  'loanMaturity',
  'divRepaymentTime',
  'selectedCouponId',
  'selectedDebtContractNos',
  'clearCardBankStates',
  'clearCardDraftBankStates',
  'clearCardCompVisible',
  'lowIncomeData',
  'isCheckDcSwitch',
]);

const CLEAR_CARD_STABLE_STATE_KEYS = new Set(['matchBy', 'value', 'checked', 'bankLoanAmt']);
const CLEAR_CARD_BANK_AND_MASK_STATE_KEYS = new Set([
  'matchBy',
  'bankId',
  'bankCardNum1',
  'checked',
  'bankLoanAmt',
]);

const OUTSTANDING_LOAN_KEYS = new Set([
  'loanInstitutionCode',
  'loanInstitutionName',
  'loanType',
  'loanTypeName',
  'loanAmt',
  'remainingPeriods',
  'monthlyRepaymentAmt',
  'outstandingLoanAmt',
]);

const LOW_INCOME_DATA_KEYS = new Set(['outUnsecuredLoanFlag', 'outstandingLoanList']);
const EXISTING_BANK_SELECTION_KEYS = new Set(['source', 'matchBy', 'value']);

const MANUAL_BANK_SELECTION_KEYS = new Set([
  'source',
  'bankCardCode',
  'bankCardName',
  'bankCardEnName',
  'bankCardNum',
]);

const isStrictCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const isExistingBankSelection = (value: unknown): value is ExistingCashoutBankSelection =>
  isRecord(value) &&
  !Object.keys(value).some((key) => !EXISTING_BANK_SELECTION_KEYS.has(key)) &&
  value.source === 'existing' &&
  (value.matchBy === 'id' || value.matchBy === 'bankCardNumEncrypt') &&
  typeof value.value === 'string' &&
  value.value.trim().length > 0;

const isManualBankSelection = (value: unknown): value is ManualCashoutBankSelection =>
  isRecord(value) &&
  !Object.keys(value).some((key) => !MANUAL_BANK_SELECTION_KEYS.has(key)) &&
  value.source === 'manual' &&
  typeof value.bankCardCode === 'string' &&
  value.bankCardCode.length > 0 &&
  typeof value.bankCardName === 'string' &&
  value.bankCardName.length > 0 &&
  typeof value.bankCardNum === 'string' &&
  value.bankCardNum.length > 0 &&
  (value.bankCardEnName === undefined || typeof value.bankCardEnName === 'string');

const hasOnlyKeys = (value: Record<string, unknown>, allowedKeys: Set<string>) =>
  !Object.keys(value).some((key) => !allowedKeys.has(key));

const isClearCardBankState = (value: unknown): value is ClearCardBankState => {
  if (
    !isRecord(value) ||
    typeof value.checked !== 'boolean' ||
    typeof value.bankLoanAmt !== 'number' ||
    !Number.isFinite(value.bankLoanAmt) ||
    value.bankLoanAmt < 0
  ) {
    return false;
  }

  if (
    value.matchBy === 'id' ||
    value.matchBy === 'bankCardNumEncrypt' ||
    value.matchBy === 'token'
  ) {
    return (
      hasOnlyKeys(value, CLEAR_CARD_STABLE_STATE_KEYS) &&
      typeof value.value === 'string' &&
      value.value.trim().length > 0
    );
  }

  return (
    value.matchBy === 'bankIdAndBankCardNum1' &&
    hasOnlyKeys(value, CLEAR_CARD_BANK_AND_MASK_STATE_KEYS) &&
    typeof value.bankId === 'string' &&
    value.bankId.trim().length > 0 &&
    typeof value.bankCardNum1 === 'string' &&
    value.bankCardNum1.trim().length > 0
  );
};

const isOutstandingLoanCacheItem = (value: unknown): value is OutstandingLoanCacheItem =>
  isRecord(value) &&
  hasOnlyKeys(value, OUTSTANDING_LOAN_KEYS) &&
  [...OUTSTANDING_LOAN_KEYS].every((key) => typeof value[key] === 'string');

const isLowIncomeCacheData = (value: unknown): value is LowIncomeCacheData =>
  isRecord(value) &&
  hasOnlyKeys(value, LOW_INCOME_DATA_KEYS) &&
  (value.outUnsecuredLoanFlag === 'Y' || value.outUnsecuredLoanFlag === 'N') &&
  Array.isArray(value.outstandingLoanList) &&
  value.outstandingLoanList.every(isOutstandingLoanCacheItem);
const isValidProductCache = (value: unknown): value is UnionLoanCacheData => {
  if (!isRecord(value) || Object.keys(value).some((key) => !CACHE_KEYS.has(key))) return false;
  return (
    (value.cashoutAmount === undefined ||
      (Number.isFinite(value.cashoutAmount) && (value.cashoutAmount as number) >= 0)) &&
    (value.cashoutBankSelection === undefined ||
      isExistingBankSelection(value.cashoutBankSelection) ||
      isManualBankSelection(value.cashoutBankSelection)) &&
    (value.loanMaturity === undefined ||
      (Number.isFinite(value.loanMaturity) && (value.loanMaturity as number) > 0)) &&
    (value.divRepaymentTime === undefined || isStrictCalendarDate(value.divRepaymentTime)) &&
    (value.selectedCouponId === undefined ||
      value.selectedCouponId === null ||
      (typeof value.selectedCouponId === 'string' && value.selectedCouponId.trim().length > 0)) &&
    (value.selectedDebtContractNos === undefined ||
      (Array.isArray(value.selectedDebtContractNos) &&
        value.selectedDebtContractNos.every(
          (item) => typeof item === 'string' && item.trim().length > 0
        ))) &&
    (value.clearCardBankStates === undefined ||
      (Array.isArray(value.clearCardBankStates) &&
        value.clearCardBankStates.every(isClearCardBankState))) &&
    (value.clearCardDraftBankStates === undefined ||
      (Array.isArray(value.clearCardDraftBankStates) &&
        value.clearCardDraftBankStates.every(isClearCardBankState))) &&
    (value.clearCardCompVisible === undefined || typeof value.clearCardCompVisible === 'boolean') &&
    (value.lowIncomeData === undefined || isLowIncomeCacheData(value.lowIncomeData)) &&
    (value.isCheckDcSwitch === undefined || typeof value.isCheckDcSwitch === 'boolean')
  );
};

export const readUnionLoanProductCache = (
  value: unknown,
  productType: UnionLoanProductType
): UnionLoanCacheData => {
  if (!isRecord(value)) {
    return {};
  }

  const productCache = value[productType];
  return isValidProductCache(productCache) ? { ...productCache } : {};
};

const unionLoanCacheSlice = createSlice({
  name: 'unionLoanCache',
  initialState,
  reducers: {
    setUnionLoanCache: (
      state,
      action: PayloadAction<{ productType: UnionLoanProductType; data: UnionLoanCacheData }>
    ) => {
      state[action.payload.productType] = action.payload.data;
    },
    clearUnionLoanCache: (state, action: PayloadAction<{ productType: UnionLoanProductType }>) => {
      state[action.payload.productType] = {};
    },
  },
});

export const { setUnionLoanCache, clearUnionLoanCache } = unionLoanCacheSlice.actions;
export default unionLoanCacheSlice.reducer;
